/**
 * SAMA's vocabulary (P415).
 *
 * THE ENGLISH HERE IS WHAT THE PRODUCT ALREADY RENDERS, verbatim. That
 * is not a convenience: Track J's whole claim is that SAMA is
 * re-expressed through the regime object with nothing the Kingdom sees
 * changing, and this file is where that is either true or not. Every
 * value was taken from the string the catalogue renders today.
 *
 * The Arabic is `markUntranslated` scaffold like the rest of `ar.ts` --
 * the standing rule since P127 is that we do not fabricate Arabic, and
 * these are exactly the terms E1 says a human must settle first
 * (Important Business Service, Impact tolerance, Minimum Viable Bank
 * are three of the thirteen `decision_needed` glossary rows).
 */
import { markUntranslated } from "shared/i18n/markUntranslated";

import type { RegimeVocab, RegimeVocabByLanguage } from "shared/i18n/regime/types";

const en: RegimeVocab = {
  serviceTerm: "important business service",
  serviceTermPlural: "important business services",
  serviceTermShort: "IBS",
  serviceTermShortPlural: "IBSes",
  serviceTermShortA: "an IBS",
  serviceTermTitle: "Important Business Service",
  serviceTermPluralTitle: "Important Business Services",
  serviceTermDefinition:
    "A service whose disruption would cause unacceptable harm to " +
    "customers, to market integrity, or to the safety and soundness of " +
    "the bank.",

  toleranceTerm: "impact tolerance",
  toleranceTermPlural: "impact tolerances",
  toleranceTermTitle: "Impact Tolerance",
  riskLimitTerm: "headroom",

  institutionTerm: "bank",
  institutionTermPlural: "banks",
  institutionClassTerm: "Member Organisation",
  regulatorName: "Saudi Central Bank",
  regulatorShort: "SAMA",
  examinerTerm: "examiner",
  letterTerm: "regulator letter",

  mvbTerm: "minimum viable bank",
  mvbTermTitle: "Minimum Viable Bank",
  mvbTermShort: "MVB",

  admissionPathway: {
    ces: "Critical Economic Service",
    ids: "Indirect Delivery Service",
    // SAMA runs two streams and has no third. Named rather than left
    // blank so the shape stays closed and a screen that asks for it
    // gets a true answer instead of an empty string.
    critical_operation: "not used under this regime",
  },

  outcomeCategory: {
    minimize_customer_harm: "Minimise customer harm",
    retain_customer_confidence: "Retain customer confidence",
    regulatory_engagement: "Regulatory engagement",
    protect_safety_and_soundness: "Protect safety and soundness",
  },
  outcomeCategoryDefinition: {
    minimize_customer_harm:
      "Disruption would cause customers financial loss, exclusion from " +
      "their money, or harm they cannot avoid.",
    retain_customer_confidence:
      "Disruption would damage trust in the bank or in the payment " +
      "system it participates in.",
    regulatory_engagement:
      "Disruption would breach an obligation the regulator supervises " +
      "directly, or stop the bank reporting to it.",
    protect_safety_and_soundness:
      "Disruption would threaten the bank's own viability or the " +
      "stability of the wider system.",
  },

  boardPackTitle: "Board resilience snapshot",
  currencySymbol: "SAR",
};

export const sama: RegimeVocabByLanguage = {
  en,
  // Scaffold, not translation. `markUntranslated` wraps every leaf in
  // guillemets so an Arabic screen reads as UNTRANSLATED rather than
  // as English somebody forgot -- the P127 decision, unchanged.
  ar: markUntranslated(en) as RegimeVocab,
};
